import React, { useCallback, type SyntheticEvent } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import Button from '@atlaskit/button';
import MediaServicesZoomInIcon from '@atlaskit/icon/core/migration/zoom-in--media-services-zoom-in';
import MediaServicesZoomOutIcon from '@atlaskit/icon/core/migration/zoom-out--media-services-zoom-out';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import Range from '@atlaskit/range';
import { useIntl } from '@atlassian/jira-intl';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	LINEAR_ZOOM_STEP,
	MAXIMUM_LINEAR_ZOOM,
	MINIMUM_LINEAR_ZOOM,
	getExponentialZoom,
	getLinearZoom,
	isMaximumZoom,
	isMinimumZoom,
} from './utils.tsx';
import messages from './messages.tsx';

type Props = {
	onZoomChange: (zoom: number) => void;
	zoom: number;
};

export const ZoomControls = ({ onZoomChange, zoom }: Props) => {
	const { formatMessage } = useIntl();
	const changeZoom = useCallback(
		(linearZoom: number) => {
			const exponentialZoom = getExponentialZoom(linearZoom);
			onZoomChange(exponentialZoom);
		},
		[onZoomChange],
	);

	const onZoomIn = useCallback(
		(e: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			const linearZoom = getLinearZoom(zoom) + LINEAR_ZOOM_STEP;
			changeZoom(linearZoom);
			fireUIAnalytics(analyticsEvent, 'zoomIn');
		},
		[changeZoom, zoom],
	);

	const onZoomOut = useCallback(
		(e: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			const linearZoom = getLinearZoom(zoom) - LINEAR_ZOOM_STEP;
			changeZoom(linearZoom);
			fireUIAnalytics(analyticsEvent, 'zoomOut');
		},
		[changeZoom, zoom],
	);
	return (
		<Box xcss={zoomControlContainerStyles}>
			<Box xcss={zoomControlsStyles}>
				<Button
					isDisabled={isMinimumZoom(getLinearZoom(zoom))}
					testId="workflow-view-diagram-modal.ui.zoom-controls.zoom-out-button"
					appearance="subtle"
					iconBefore={
						<MediaServicesZoomOutIcon
							LEGACY_size="medium"
							spacing="spacious"
							color={token('color.icon')}
							label=""
						/>
					}
					onClick={onZoomOut}
					aria-label={formatMessage(messages.zoomOut)}
					interactionName="auto-generated.workflow-readonly-modal.ui.diagram.zoom-out-button.button"
				/>
				<Range
					min={MINIMUM_LINEAR_ZOOM}
					max={MAXIMUM_LINEAR_ZOOM}
					step={LINEAR_ZOOM_STEP}
					value={getLinearZoom(zoom)}
					onChange={changeZoom}
					aria-label={formatMessage(messages.zoomSlider)}
				/>
				<Button
					testId="workflow-view-diagram-modal.ui.zoom-controls.zoom-in-button"
					isDisabled={isMaximumZoom(getLinearZoom(zoom))}
					appearance="subtle"
					iconBefore={
						<MediaServicesZoomInIcon
							LEGACY_size="medium"
							spacing="spacious"
							color={token('color.icon')}
							label=""
						/>
					}
					onClick={onZoomIn}
					aria-label={formatMessage(messages.zoomIn)}
					interactionName="auto-generated.workflow-readonly-modal.ui.diagram.zoom-in-button.button1"
				/>
			</Box>
		</Box>
	);
};

const zoomControlContainerStyles = xcss({
	position: 'relative',
});

const zoomControlsStyles = xcss({
	position: 'absolute',
	bottom: 'space.100',
	right: 'space.100',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	width: `${gridSize * 25}px`,
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',

	backgroundColor: 'color.background.neutral',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	height: `${gridSize * 5}px`,
	justifyContent: 'space-evenly',
	paddingTop: 'space.0',
	paddingRight: 'space.050',
	paddingBottom: 'space.0',
	paddingLeft: 'space.050',
});
